import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, XCircle, ExternalLink, Server, Mail, Clock, Save, Send, Loader2 } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

export default function Settings() {
  const [config, setConfig] = useState(null);
  const [sources, setSources] = useState([]);
  const [form, setForm] = useState({
    gmail_user: "",
    gmail_app_password: "",
    digest_to_email: "",
    scan_interval_hours: 6,
    digest_hour: 8,
    digest_minute: 0,
  });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const load = async () => {
    const [c, s] = await Promise.all([api.get("/config"), api.get("/sources")]);
    setConfig(c.data);
    setSources(s.data);
    setForm((f) => ({
      ...f,
      gmail_user: c.data.gmail_user || "",
      digest_to_email: c.data.digest_to_email || "",
      scan_interval_hours: c.data.scan_interval_hours,
      digest_hour: c.data.digest_hour,
      digest_minute: c.data.digest_minute,
    }));
  };
  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      // Only send app password if user typed something (empty = keep existing)
      if (!payload.gmail_app_password) delete payload.gmail_app_password;
      const { data } = await api.put("/settings", payload);
      setConfig(data);
      setForm((f) => ({ ...f, gmail_app_password: "" }));
      toast.success("Ustawienia zapisane");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const testEmail = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/settings/test-email");
      if (data.success) toast.success("Testowy email wysłany!");
      else toast.error(data.error || "Nie udało się wysłać");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setTesting(false);
    }
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div className="p-8 lg:p-12 max-w-4xl">
      <div className="mb-8">
        <div className="text-[11px] uppercase tracking-[0.25em] text-cyan-400 font-bold mb-2">Konfiguracja</div>
        <h1 className="font-display text-4xl font-semibold tracking-tight text-slate-50">Ustawienia</h1>
        <p className="text-slate-400 mt-2 text-sm">
          Zapisywane w bazie, aktywne natychmiast — bez restartu backendu.
        </p>
      </div>

      <div className="grid gap-6">
        {/* Gmail */}
        <div className="rounded-xl border border-slate-800 bg-slate-900/50 p-6">
          <div className="flex items-center gap-3 mb-5">
            <Mail className="w-4 h-4 text-cyan-400" />
            <h2 className="font-display text-lg font-medium">Gmail SMTP</h2>
            {config?.gmail_configured ? (
              <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-300 border border-emerald-500/30">
                <CheckCircle2 className="w-3 h-3" /> Aktywne
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-red-500/10 text-red-300 border border-red-500/30">
                <XCircle className="w-3 h-3" /> Wymaga konfiguracji
              </span>
            )}
          </div>

          {!config ? (
            <Skeleton className="h-40 bg-slate-800/60" />
          ) : (
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs uppercase tracking-[0.2em] text-slate-400">Gmail</Label>
                <Input
                  data-testid="settings-gmail-user"
                  value={form.gmail_user}
                  onChange={(e) => set("gmail_user", e.target.value)}
                  placeholder="ty@gmail.com"
                  className="mt-2 h-11 bg-slate-950/60 border-slate-800 focus-visible:ring-cyan-400"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.2em] text-slate-400">App Password</Label>
                <Input
                  data-testid="settings-gmail-password"
                  type="password"
                  value={form.gmail_app_password}
                  onChange={(e) => set("gmail_app_password", e.target.value)}
                  placeholder={config?.gmail_configured ? "•••••••••••••••• (zapisane)" : "16 znaków bez spacji"}
                  className="mt-2 h-11 bg-slate-950/60 border-slate-800 focus-visible:ring-cyan-400"
                />
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs uppercase tracking-[0.2em] text-slate-400">
                  Wysyłaj digest na (opcjonalnie)
                </Label>
                <Input
                  data-testid="settings-digest-to"
                  value={form.digest_to_email}
                  onChange={(e) => set("digest_to_email", e.target.value)}
                  placeholder="domyślnie = Gmail powyżej"
                  className="mt-2 h-11 bg-slate-950/60 border-slate-800 focus-visible:ring-cyan-400"
                />
              </div>
            </div>
          )}

          <div className="mt-5 flex flex-wrap items-center justify-between gap-3 pt-4 border-t border-slate-800">
            <a
              href="https://myaccount.google.com/apppasswords"
              target="_blank"
              rel="noreferrer"
              className="text-xs text-cyan-400 hover:text-cyan-300 inline-flex items-center gap-1"
            >
              Jak wygenerować App Password? <ExternalLink className="w-3 h-3" />
            </a>
            <div className="flex gap-2">
              <Button
                data-testid="settings-test-email"
                onClick={testEmail}
                disabled={testing || !config?.gmail_configured}
                variant="outline"
                className="h-10 border-slate-700 hover:border-cyan-400/50 hover:text-cyan-300"
              >
                {testing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                Wyślij testowy email
              </Button>
              <Button
                data-testid="settings-save"
                onClick={save}
                disabled={saving}
                className="h-10 bg-cyan-400 hover:bg-cyan-300 text-slate-950 font-semibold"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                Zapisz
              </Button>
            </div>
          </div>
        </div>

        {/* Harmonogram */}
        <div className="rounded-xl border border-slate-800 bg-slate-900/50 p-6">
          <div className="flex items-center gap-3 mb-5">
            <Clock className="w-4 h-4 text-cyan-400" />
            <h2 className="font-display text-lg font-medium">Harmonogram</h2>
          </div>
          {!config ? (
            <Skeleton className="h-24 bg-slate-800/60" />
          ) : (
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label className="text-xs uppercase tracking-[0.2em] text-slate-400">Skan co (h)</Label>
                <Input
                  data-testid="settings-scan-hours"
                  type="number"
                  min={1}
                  max={72}
                  value={form.scan_interval_hours}
                  onChange={(e) => set("scan_interval_hours", parseInt(e.target.value) || 1)}
                  className="mt-2 h-11 bg-slate-950/60 border-slate-800 focus-visible:ring-cyan-400"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.2em] text-slate-400">Digest godz.</Label>
                <Input
                  data-testid="settings-digest-hour"
                  type="number"
                  min={0}
                  max={23}
                  value={form.digest_hour}
                  onChange={(e) => set("digest_hour", parseInt(e.target.value) || 0)}
                  className="mt-2 h-11 bg-slate-950/60 border-slate-800 focus-visible:ring-cyan-400"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.2em] text-slate-400">Digest min.</Label>
                <Input
                  data-testid="settings-digest-minute"
                  type="number"
                  min={0}
                  max={59}
                  value={form.digest_minute}
                  onChange={(e) => set("digest_minute", parseInt(e.target.value) || 0)}
                  className="mt-2 h-11 bg-slate-950/60 border-slate-800 focus-visible:ring-cyan-400"
                />
              </div>
            </div>
          )}
        </div>

        {/* Sources */}
        <div className="rounded-xl border border-slate-800 bg-slate-900/50 p-6">
          <div className="flex items-center gap-3 mb-4">
            <Server className="w-4 h-4 text-cyan-400" />
            <h2 className="font-display text-lg font-medium">Źródła (kuratoria)</h2>
          </div>
          {sources.length === 0 ? (
            <div className="text-sm text-slate-500">
              Brak jeszcze uruchomionego skanu. Kliknij „Skanuj teraz" na dashboardzie.
            </div>
          ) : (
            <div className="divide-y divide-slate-800">
              {sources.map((s) => (
                <div key={s.id} className="py-3 flex items-center justify-between" data-testid={`source-${s.id}`}>
                  <div>
                    <div className="text-sm text-slate-200">{s.name}</div>
                    <div className="text-xs text-slate-500">
                      Ostatni skan: {s.last_scan_at ? new Date(s.last_scan_at).toLocaleString("pl-PL") : "—"} ·{" "}
                      <span className="text-cyan-400">{s.last_scan_count} ofert</span>
                    </div>
                  </div>
                  <a
                    href={s.url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-xs text-cyan-400 inline-flex items-center gap-1 hover:text-cyan-300"
                  >
                    Portal <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
